"use client";

import { TableHead, TableRow } from "@/shadcn-components/ui/table";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import {
  OPEN_POSITION_COL_WIDTHS,
  openPositionsTh,
} from "./openPositionsTableLayout";

export interface OpenPositionsTableHeaderProps {
  sortColumn: string | null;
  sortDirection: "asc" | "desc";
  filters: Record<string, string>;
  showFilterInputs: boolean;
  onSort: (column: string) => void;
  onFilterChange: (column: string, value: string) => void;
  renderSortIcon: (column: string) => React.ReactNode;
  renderFilterDropdown: (column: string, placeholder: string) => React.ReactNode;
}

export function OpenPositionsTableHeader({
  sortColumn,
  sortDirection,
  filters,
  showFilterInputs,
  onSort,
  onFilterChange,
  renderSortIcon,
  renderFilterDropdown,
}: OpenPositionsTableHeaderProps) {
  void sortColumn;
  void sortDirection;

  const sortButton = (column: string, label: string) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-5 gap-0 px-0.5 text-[11px] font-medium"
      onClick={() => onSort(column)}
    >
      {label}
      {renderSortIcon(column)}
    </Button>
  );

  return (
    <>
      <TableRow>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.symbol}`}>
          <div className="flex items-center">
            {sortButton("symbol", "Symbol")}
            {!showFilterInputs &&
              renderFilterDropdown("symbol", "Filter symbols...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.direction}`}>
          <div className="flex items-center">
            {sortButton("tradeType", "Direction")}
            {!showFilterInputs &&
              renderFilterDropdown("tradeType", "BUY/SELL...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.lots}`}>
          <div className="flex items-center">
            {sortButton("lots", "Lots")}
            {!showFilterInputs && renderFilterDropdown("lots", "Filter lots...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.ticket}`}>
          <div className="flex items-center">
            {sortButton("ticket", "Ticket")}
            {!showFilterInputs &&
              renderFilterDropdown("ticket", "Filter tickets...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.openPrice}`}>
          <div className="flex items-center">
            {sortButton("openPrice", "Open Price")}
            {!showFilterInputs &&
              renderFilterDropdown("openPrice", "Filter prices...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.tp}`}>
          <div className="flex items-center">
            {sortButton("takeProfit", "TP")}
            {!showFilterInputs && renderFilterDropdown("takeProfit", "Filter TP...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.sl}`}>
          <div className="flex items-center">
            {sortButton("stopLoss", "SL")}
            {!showFilterInputs && renderFilterDropdown("stopLoss", "Filter SL...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.current}`}>
          <div className="flex items-center">
            {sortButton("liveCurrentPrice", "Current Price")}
            {!showFilterInputs &&
              renderFilterDropdown("liveCurrentPrice", "Filter prices...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.change}`}>
          <div className="flex items-center">
            {sortButton("priceChange", "Change")}
            {!showFilterInputs &&
              renderFilterDropdown("priceChange", "Filter change...")}
          </div>
        </TableHead>
        <TableHead className={`${openPositionsTh} ${OPEN_POSITION_COL_WIDTHS.pnl}`}>
          <div className="flex items-center">
            {sortButton("liveProfit", "P&L")}
            {!showFilterInputs &&
              renderFilterDropdown("liveProfit", "Filter P&L...")}
          </div>
        </TableHead>
        <TableHead
          className={`${openPositionsTh} text-right ${OPEN_POSITION_COL_WIDTHS.actions}`}
        >
          <span className="text-[11px] text-muted-foreground">Actions</span>
        </TableHead>
      </TableRow>

      {showFilterInputs && (
        <TableRow className="border-b bg-background">
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.symbol}`}>
            <Input
              placeholder="Filter symbols..."
              value={filters.symbol || ""}
              onChange={(e) => onFilterChange("symbol", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.direction}`}>
            <Input
              placeholder="BUY/SELL..."
              value={filters.tradeType || ""}
              onChange={(e) => onFilterChange("tradeType", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.lots}`}>
            <Input
              placeholder="Filter lots..."
              value={filters.lots || ""}
              onChange={(e) => onFilterChange("lots", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.ticket}`}>
            <Input
              placeholder="Filter tickets..."
              value={filters.ticket || ""}
              onChange={(e) => onFilterChange("ticket", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.openPrice}`}>
            <Input
              placeholder="Filter prices..."
              value={filters.openPrice || ""}
              onChange={(e) => onFilterChange("openPrice", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.tp}`}>
            <Input
              placeholder="Filter TP..."
              value={filters.takeProfit || ""}
              onChange={(e) => onFilterChange("takeProfit", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.sl}`}>
            <Input
              placeholder="Filter SL..."
              value={filters.stopLoss || ""}
              onChange={(e) => onFilterChange("stopLoss", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.current}`}>
            <Input
              placeholder="Filter prices..."
              value={filters.liveCurrentPrice || ""}
              onChange={(e) => onFilterChange("liveCurrentPrice", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.change}`}>
            <Input
              placeholder="Filter change..."
              value={filters.priceChange || ""}
              onChange={(e) => onFilterChange("priceChange", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.pnl}`}>
            <Input
              placeholder="Filter P&L..."
              value={filters.liveProfit || ""}
              onChange={(e) => onFilterChange("liveProfit", e.target.value)}
              className="h-5 px-1.5 text-[11px]"
            />
          </TableHead>
          <TableHead className={`h-7 px-1.5 py-0.5 ${OPEN_POSITION_COL_WIDTHS.actions}`} />
        </TableRow>
      )}
    </>
  );
}
